import React, { useEffect, useState } from "react";
import { useFormikContext, FieldArray } from "formik";
import { Row, Col, Form } from "react-bootstrap";
import CustomAutoCompleteProductKey from "../../../../../components/formik/CustomAutoCompleteProductKey";
import CustomAutoCompleteUnitKey from "../../../../../components/formik/CustomAutoCompleteUnitKey";

import { findProductKeyByCoincidence } from "../../../../../services/CatalogService";
import { findUnitKeyByCoincidence } from "../../../../../services/CatalogService";

import axiosClient from "../../../../../config/axios";

export default function TableConceptsInvoice({
  valuesKey = "conceptosFacturaTabla",
  handleChange,
  selectedProductKey,
  setSelectedProductKey,
  touched,
  errors,
  orden,
}) {
  const { values, setFieldValue } = useFormikContext();
  const [autoCompleteKey, setAutoCompleteKey] = useState(0);

  const [initialized, setInitialized] = useState(false);
  const [defaultsSet, setDefaultsSet] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!initialized && values[valuesKey]?.length === 0) {
      setFieldValue(valuesKey, [
        {
          cantidad: 1,
          productKey: null,
          unitKey: null,
          precioUnitario: "",
          iva: "",
          total: "",
          needRetention: false,
        },
      ]);
      setInitialized(true);
    }
  }, [initialized, values, valuesKey, setFieldValue]);

  const handleCalculateTotal = (index) => {
    const cantidadRaw = values[valuesKey][index].cantidad;
    const cantidad = parseFloat(cantidadRaw);
    const precioUnitarioRaw = values[valuesKey][index].precioUnitario;

    // Validar si precioUnitario está vacío o null
    if (precioUnitarioRaw === "" || precioUnitarioRaw === null) {
      setFieldValue(`${valuesKey}[${index}].iva`, "0.00");
      setFieldValue(`${valuesKey}[${index}].total`, "0.00");
      return;
    }

    const precioUnitario = parseFloat(precioUnitarioRaw);

    // Si la cantidad no es válida
    if (!cantidadRaw || Number.isNaN(cantidad) || cantidad <= 0) {
      setFieldValue(`${valuesKey}[${index}].iva`, "0.00");
      setFieldValue(`${valuesKey}[${index}].total`, "0.00");
      return;
    }

    // Si el precio unitario no es válido
    if (Number.isNaN(precioUnitario) || precioUnitario <= 0) {
      setFieldValue(`${valuesKey}[${index}].iva`, "0.00");
      setFieldValue(`${valuesKey}[${index}].total`, "0.00");
      return;
    }

    // Calcular subtotal, IVA y total
    const subtotal = cantidad * precioUnitario;
    const iva = subtotal * 0.16;
    
    // Aplicamos la retención si está activada
    const needRetention = values[valuesKey][index].needRetention || false;
    const retention = needRetention ? subtotal * 0.04 : 0;

    const total = subtotal + iva - retention;

    setFieldValue(`${valuesKey}[${index}].iva`, iva.toFixed(2));
    setFieldValue(`${valuesKey}[${index}].total`, total.toFixed(2));
  };

  //console.log("Orden :", orden);

  useEffect(() => {
    const fetchAndSetProductKeyAndUnit = async () => {
      if (
        orden?.typeService?.id &&
        values[valuesKey]?.length > 0 &&
        !values[valuesKey][0].productKey
      ) {
        try {
          setLoading(true);
          const response = await axiosClient.get(
            `/typeServices/${orden.typeService.id}`
          );
          const { id_productKey, id_unitKey } = response.data;

          if (id_productKey) {
            const [productKey] = await findProductKeyByCoincidence(
              id_productKey
            );
            if (productKey) {
              setFieldValue(`${valuesKey}[0].productKey`, productKey);
            }
          }

          if (id_unitKey) {
            const [unitKey] = await findUnitKeyByCoincidence(id_unitKey);
            if (unitKey) {
              setFieldValue(`${valuesKey}[0].unitKey`, unitKey);
            }
          }
        } catch (error) {
          console.error("Error al obtener datos de tipo de servicio:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAndSetProductKeyAndUnit();
  }, [orden, values, setFieldValue]);

  const [precioUnitarioInicializado, setPrecioUnitarioInicializado] =
    useState(false);

  //Calcula costos iniciales
  useEffect(() => {
    if (
      orden?.total &&
      values[valuesKey]?.length > 0 &&
      !precioUnitarioInicializado
    ) {
      const total = parseFloat(orden.total);
      const precioUnitario = (total / 1.16).toFixed(2);
      const iva = (total - precioUnitario).toFixed(2);

      setFieldValue(`${valuesKey}[0].precioUnitario`, precioUnitario);
      setFieldValue(`${valuesKey}[0].iva`, iva);
      setFieldValue(`${valuesKey}[0].total`, total.toFixed(2));
      setPrecioUnitarioInicializado(true);
    }
  }, [orden?.total, values, setFieldValue, precioUnitarioInicializado]);

  //Recalcula cambios en cantidad y precio unitario
  useEffect(() => {
    values[valuesKey]?.forEach((_, index) => {
      handleCalculateTotal(index);
    });
  }, [values[valuesKey]]);

  if (loading) {
    return (
      <div className="text-center my-4">
        <i className="fa fa-spinner fa-spin fa-3x"></i>
        <p>Cargando...</p>
      </div>
    );
  }
  return (
    <FieldArray
      name={valuesKey}
      render={(arrayHelpers) => (
        <>
          <div
            className="table-responsive"
            style={{ minHeight: "200px", overflowX: "auto" }}
          >
            <table
              className="table table-hover align-middle mb-0"
              style={{ minWidth: "800px" }}
            >
              <thead className="table-light text-center">
                <tr>
                  <th></th>
                  <th>Cantidad</th>
                  <th>Producto</th>
                  <th>Unidad</th>
                  <th>Precio unitario</th>
                  <th>IVA</th>
                  <th>Retención (4%)</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {values[valuesKey]?.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        name={`${valuesKey}[${index}].cantidad`}
                        value={item.cantidad}
                        onChange={(e) => {
                          setFieldValue(
                            `${valuesKey}[${index}].cantidad`,
                            e.target.value
                          );
                          handleCalculateTotal(index);
                        }}
                      />
                      {touched?.[valuesKey]?.[index]?.cantidad &&
                        errors?.[valuesKey]?.[index]?.cantidad && (
                          <div className="text-danger">
                            {errors[valuesKey][index].cantidad}
                          </div>
                        )}
                    </td>
                    <td style={{ width: "300px" }}>
                      <CustomAutoCompleteProductKey
                        key={autoCompleteKey + index}
                        widthClass="w-100"
                        hideLabel={true}
                        formik={{
                          values: {
                            productKey: values[valuesKey][index].productKey,
                          },
                          setFieldValue: (field, value) => {
                            if (field === "productKey") {
                              setFieldValue(
                                `${valuesKey}[${index}].productKey`,
                                value
                              );
                            }
                          },
                          touched: touched?.[valuesKey]?.[index],
                          errors: errors?.[valuesKey]?.[index],
                        }}
                        field="productKey"
                        labelAlign="left"
                      />
                    </td>
                    <td style={{ width: "300px" }}>
                      <CustomAutoCompleteUnitKey
                        key={autoCompleteKey + index}
                        widthClass="w-100"
                        hideLabel={true}
                        formik={{
                          values: {
                            unitKey: values[valuesKey][index].unitKey,
                          },
                          setFieldValue: (field, value) => {
                            if (field === "unitKey") {
                              setFieldValue(
                                `${valuesKey}[${index}].unitKey`,
                                value
                              );
                            }
                          },
                          touched: touched?.[valuesKey]?.[index],
                          errors: errors?.[valuesKey]?.[index],
                        }}
                        field="unitKey"
                        labelAlign="left"
                      />
                    </td>

                    <td style={{ width: "150px" }}>
                      <Form.Control
                        type="number"
                        name={`${valuesKey}[${index}].precioUnitario`}
                        value={item.precioUnitario}
                        onChange={(e) => {
                          setFieldValue(
                            `${valuesKey}[${index}].precioUnitario`,
                            e.target.value
                          );
                          handleCalculateTotal(index);
                        }}
                      />
                      {touched?.[valuesKey]?.[index]?.precioUnitario &&
                        errors?.[valuesKey]?.[index]?.precioUnitario && (
                          <div className="text-danger">
                            {errors[valuesKey][index].precioUnitario}
                          </div>
                        )}
                    </td>
                    <td style={{ width: "150px" }}>
                      <Form.Control
                        type="number"
                        name={`${valuesKey}[${index}].iva`}
                        value={item.iva}
                        disabled
                      />
                      {touched?.[valuesKey]?.[index]?.iva &&
                        errors?.[valuesKey]?.[index]?.iva && (
                          <div className="text-danger">
                            {errors[valuesKey][index].iva}
                          </div>
                        )}
                    </td>
                    <td style={{ width: "150px" }}>
                      <div className="d-flex justify-content-center">
                        <Form.Check
                          type="switch"
                          id={`needRetention-${index}`}
                          name={`${valuesKey}[${index}].needRetention`}
                          checked={item.needRetention || false}
                            onChange={(e) => {
                              setFieldValue(`${valuesKey}[${index}].needRetention`, e.target.checked);
                              handleCalculateTotal(index); // recalcula al cambiar el switch
                            }}
                          label=""
                        />
                      </div>
                    </td>
                    <td style={{ width: "150px" }}>
                      <Form.Control
                        type="text"
                        name={`${valuesKey}[${index}].total`}
                        value={item.total ? `$${item.total}` : "$0.00"}
                        disabled
                        className="text-end"
                      />
                      {touched?.[valuesKey]?.[index]?.total &&
                        errors?.[valuesKey]?.[index]?.total && (
                          <div className="text-danger">
                            {errors[valuesKey][index].total}
                          </div>
                        )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={8}>
                    <div className="d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-success btn-icon btn-circle btn-sm me-2"
                        onClick={() => {
                          const baseConcept = values[valuesKey][0] || {};
                          arrayHelpers.push({
                            cantidad: 1,
                            productKey: baseConcept.productKey || null,
                            unitKey: baseConcept.unitKey || null,
                            precioUnitario: "",
                            iva: "",
                            total: "",
                            needRetention: false,
                          });
                        }}
                      >
                        <i className="fa fa-plus"></i>
                      </button>
                      <span>Agregar concepto</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    />
  );
}
